import { GOOGLE_PRODUCTS, GoogleProduct } from '@api/api/alfred-integrations';
import { config } from '@terminal/config';
import {
  deleteGoogleDisconnect,
  postGoogleConnect,
} from '@terminal/services/alfred-intergations/alfred-integrations-service';
import { useOAuthPkceConnect } from './useOAuthPkceConnect';

export const isValidGoogleProductName = (
  serviceName?: string
): serviceName is GoogleProduct =>
  GOOGLE_PRODUCTS.some(product => product === serviceName);

interface UseGoogleConnectProps {
  refetchServices: () => void;
}

const getRequestedScopes = (service: string) =>
  [
    ...config.google.baseScopes,
    ...(isValidGoogleProductName(service)
      ? config.google.scopesByProduct[service]
      : []),
  ].join(' ');

export const useGoogleConnect = ({
  refetchServices,
}: UseGoogleConnectProps) => {
  const { connect, disconnect } = useOAuthPkceConnect({
    serviceName: 'google',
    displayName: 'Google',
    redirectUri: config.google.redirectUri,
    buildAuthorizeUrl: ({ state, codeChallenge, service }) => {
      const authParams = new URLSearchParams({
        client_id: config.google.clientId,
        response_type: 'code',
        redirect_uri: config.google.redirectUri,
        scope: getRequestedScopes(service),
        state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        access_type: 'offline',
        prompt: 'consent',
        // Without it, connecting a product later replaces the earlier grant.
        include_granted_scopes: 'true',
      });

      return `${config.google.authorizeUrl}?${authParams}`;
    },
    postConnect: (body, service) =>
      postGoogleConnect({
        ...body,
        services: [service].filter(isValidGoogleProductName),
      }),
    deleteDisconnect: deleteGoogleDisconnect,
    refetchServices,
  });

  return {
    connectGoogle: (product: GoogleProduct) => connect(product),
    disconnectGoogle: (product: GoogleProduct, deleteConversations: boolean) =>
      disconnect(deleteConversations, product),
  };
};
